/**
 * ToolsGrid - the six doors out of the dashboard.
 *
 * Every tile used to carry its own colour: indigo research, sky reviews, teal
 * tables, cyan data sources, slate templates. Six saturated hues in one row
 * is a toy box, and none of those ramps are brand tokens - they were the
 * loudest thing on a page whose whole point is quiet glass. The tiles are now
 * ink on glass with a muted glyph, and the ONLY colour is a champagne edge and
 * glyph on hover: the accent marks the thing you are about to do, which is
 * exactly the scale champagne is for.
 *
 * The tools list itself is unchanged, and `accessibleTools` is still exported
 * so the registry's `requires` and this render can never disagree about
 * whether there is anything to show.
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { isPathHiddenInCurrentEnv } from '../../../config/defaultMenu';
import GlassCard from '../../shared/brand/GlassCard';
import {
  PiListMagnifyingGlassLight,
  PiFileMagnifyingGlassLight,
  PiTableLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiBookOpenTextLight,
  PiArrowRightLight,
  PiWrenchLight,
} from 'react-icons/pi';

export interface ToolDef {
  name: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  section: string;
}

export const TOOLS: ToolDef[] = [
  {
    name: 'Research',
    desc: 'AI-powered legal research',
    icon: PiListMagnifyingGlassLight,
    href: '/research',
    section: 'research',
  },
  {
    name: 'Reviews',
    desc: 'AI document review',
    icon: PiFileMagnifyingGlassLight,
    href: '/reviews',
    section: 'reviews',
  },
  {
    name: 'Tables',
    desc: 'AI due diligence tables',
    icon: PiTableLight,
    href: '/tables',
    section: 'tables',
  },
  {
    name: 'Data Sources',
    desc: 'Knowledge base & RAG',
    icon: PiDatabaseLight,
    href: '/data-sources',
    section: 'data_sources',
  },
  {
    name: 'Templates',
    desc: 'Document templates',
    icon: PiFileTextLight,
    href: '/templates',
    section: 'templates',
  },
  {
    name: 'Playbooks',
    desc: 'Compliance playbooks',
    icon: PiBookOpenTextLight,
    href: '/playbooks',
    section: 'reviews',
  },
];

/**
 * The tools this user can actually reach.
 *
 * Exported because the gate is needed in two places: here, to filter the tiles,
 * and in the widget registry's `requires`, so a user with no tools at all gets
 * no Tools widget rather than an empty one.
 */
export const accessibleTools = (
  hasSectionAccess: (section: string) => boolean,
): ToolDef[] => TOOLS.filter(
  (tool) => hasSectionAccess(tool.section) && !isPathHiddenInCurrentEnv(tool.href),
);

const ToolsGrid: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const visibleTools = accessibleTools(hasSectionAccess);
  if (visibleTools.length === 0) return null;

  return (
    <GlassCard icon={<PiWrenchLight />} title={t('Start something')} padding="md">
      {/*
        Measured against the WIDGET, not the window (`.widget-metrics*` in
        index.css, on the `.widget-canvas` container the grid puts around every
        cell). A `sm:`/`lg:` breakpoint here would read the viewport, so a
        quarter-width card on a 27" monitor would take the three-column branch
        and squeeze six tiles into a column 300px wide.
      */}
      <div className="widget-metrics widget-metrics-3 gap-3">
        {visibleTools.map((tool) => {
          const Icon = tool.icon;
          return (
            <button
              key={tool.href}
              type="button"
              onClick={() => history.push(tool.href)}
              className="group flex items-center gap-3 rounded-md border border-hairline-soft bg-surface-sunken/70 p-3 text-start transition-colors duration-200 ease-slide hover:border-champagne/60 focus:outline-none focus:border-champagne/60"
            >
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-surface text-fg-muted transition-colors duration-200 group-hover:text-champagne">
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t(tool.name)}
                </span>
                <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                  {t(tool.desc)}
                </span>
              </span>
              <PiArrowRightLight
                aria-hidden
                className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors duration-200 group-hover:text-champagne dark:text-gray-600 rtl:-scale-x-100"
              />
            </button>
          );
        })}
      </div>
    </GlassCard>
  );
};

export default ToolsGrid;
